/** @format */

import React from "react";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { requestBackingData, resolveExportUrl } from "./propilotApi";

// "Download backing data" affordance shown under data-backed answers. Builds
// trust by letting users pull the raw rows behind a number — a "Proof" CSV
// (unique id + date + status) and a "Full" CSV (all dimensions). Triggers an
// on-demand export against the current cached session; no extra chat turn.
const BackingDataDownload = ({ sessionId }) => {
  const accessToken = useSelector((state) => state.user?.userInfo?.accessToken);
  const [busy, setBusy] = React.useState(null); // "proof" | "full" | null
  const [result, setResult] = React.useState(null); // last successful export
  const [error, setError] = React.useState(null);

  const handleDownload = async (mode) => {
    if (busy) return;
    setBusy(mode);
    setError(null);
    try {
      const data = await requestBackingData({ sessionId, mode, token: accessToken });
      const url = resolveExportUrl(data.download_url, null);
      if (url) window.open(url, "_blank", "noopener");
      setResult({ ...data, mode });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        (detail && (detail.message || detail)) ||
          "Couldn't prepare the download. Please re-run your question and try again."
      );
    } finally {
      setBusy(null);
    }
  };

  const btnBase = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    borderRadius: "8px",
    boxShadow: "none",
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        pt: 1.25,
        borderTop: "1px dashed #e2e8f0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 0.75,
          fontSize: 11.5,
          fontWeight: 700,
          color: "#94a3b8",
        }}
      >
        Verify this answer
        <Tooltip
          arrow
          placement="top"
          title="Download the raw shipment rows behind this number — proof that the count is correct."
        >
          <InfoOutlinedIcon sx={{ fontSize: 13, color: "#cbd5e1", cursor: "help" }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!!busy}
          startIcon={
            busy === "proof" ? (
              <CircularProgress size={13} thickness={5} />
            ) : (
              <VerifiedRoundedIcon />
            )
          }
          onClick={() => handleDownload("proof")}
          sx={{
            ...btnBase,
            borderColor: "#a7f3d0",
            color: "#047857",
            backgroundColor: "#ecfdf5",
            "&:hover": { borderColor: "#6ee7b7", backgroundColor: "#d1fae5" },
          }}
        >
          {busy === "proof" ? "Preparing…" : "Proof data (CSV)"}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!!busy}
          startIcon={
            busy === "full" ? (
              <CircularProgress size={13} thickness={5} />
            ) : (
              <TableRowsRoundedIcon />
            )
          }
          onClick={() => handleDownload("full")}
          sx={{
            ...btnBase,
            borderColor: "#c7d2fe",
            color: "#4338ca",
            backgroundColor: "#eef2ff",
            "&:hover": { borderColor: "#a5b4fc", backgroundColor: "#e0e7ff" },
          }}
        >
          {busy === "full" ? "Preparing…" : "Full data (CSV)"}
        </Button>
      </Box>

      {result && !error && (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            {result.mode === "proof" ? "Proof" : "Full"} export ready —{" "}
            <Box component="strong" sx={{ color: "#334155" }}>
              {Number(result.record_count || 0).toLocaleString()} rows
            </Box>
            {result.mode === "proof" && result.columns?.length
              ? ` · columns: ${result.columns.join(", ")}`
              : ""}
            . If the download didn't start,{" "}
            <Box
              component="a"
              href={resolveExportUrl(result.download_url, null)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#4f46e5", fontWeight: 600 }}
            >
              click here
            </Box>
            .
          </Typography>
          {result.truncated && (
            <Typography
              sx={{
                mt: 0.4,
                fontSize: 11,
                fontWeight: 600,
                color: "#b45309",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
              Partial export — capped at the first{" "}
              {Number(result.row_cap || 50000).toLocaleString()} rows. The full
              dataset may be larger.
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Typography sx={{ mt: 0.75, fontSize: 11, color: "#dc2626" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BackingDataDownload;
